import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { crispWebsiteId } from "@/config";
import { useProductLine } from "@/platform/ProductLineProvider";
import {
  applySupportContext,
  bootstrapCrisp,
  openSupportChat,
  supportChatEnabled,
} from "@/lib/supportChat";

// Live-chat wiring (BRK-S136): boots the Crisp widget when — and only when — a
// website id is configured, keeps the conversation's routing key in step with
// the route, and hands the Support affordances a single `open()`.
//
// Mounted inside RootLayout's ProductLineProvider (and therefore inside
// AuthProvider), which is what lets it read the active platform and the signed-in
// broker without either being passed down. The routing key costs nothing: the
// product line is already derived from the URL (BRK-S090), so a chat opened on a
// Surety screen is tagged for Surety with no new state to keep in sync.
//
// A context rather than a hook because both shells need it — the footer's
// Contact Support renders on the marketing surface too, where there is no nav.

interface BrokerDescription {
  displayName: string | null;
  brokerageName: string | null;
}

interface SupportChatContextValue {
  /** Whether live chat is configured. False ⇒ affordances stay inert. */
  enabled: boolean;
  /** Open the chat window. A no-op when chat is not configured. */
  open: () => void;
  /**
   * Report the names the authenticated shell has already resolved, so an agent
   * opens the conversation knowing who is asking. PortalShell owns those reads;
   * duplicating them here would be a second round-trip for the same two strings.
   */
  describeBroker: (description: BrokerDescription) => void;
}

const SupportChatContext = createContext<SupportChatContextValue | null>(null);

export function SupportChatProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { productLine } = useProductLine();
  const { identity } = useAuth();
  // Read once: Vite inlines the id at build time, so it cannot change at runtime.
  const websiteId = useMemo(() => crispWebsiteId(), []);
  const enabled = supportChatEnabled(websiteId);
  const [broker, setBroker] = useState<BrokerDescription>({
    displayName: null,
    brokerageName: null,
  });

  // Boot the vendor client. bootstrapCrisp is idempotent, so a re-render (or a
  // second provider under test) cannot end up with two widgets.
  useEffect(() => {
    if (websiteId === null) return;
    bootstrapCrisp(window, websiteId);
  }, [websiteId]);

  // Keep segments, session data and identity current. Re-published on every
  // route change: the agent should see the page the broker is actually on, not
  // the one they entered the portal from.
  useEffect(() => {
    if (!enabled) return;
    applySupportContext(
      window,
      { productLine, brokerageName: broker.brokerageName, pathname },
      identity
        ? { email: identity.email, displayName: broker.displayName }
        : null,
    );
  }, [
    enabled,
    productLine,
    pathname,
    identity,
    broker.displayName,
    broker.brokerageName,
  ]);

  const describeBroker = useCallback((description: BrokerDescription) => {
    setBroker((prev) =>
      prev.displayName === description.displayName &&
      prev.brokerageName === description.brokerageName
        ? prev
        : description,
    );
  }, []);

  const open = useCallback(() => {
    if (!enabled) return;
    openSupportChat(window);
  }, [enabled]);

  const value = useMemo(
    () => ({ enabled, open, describeBroker }),
    [enabled, open, describeBroker],
  );

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
}

/** Access the support-chat controls. Safe outside the provider: chat simply
 *  reads as unconfigured, so a component can offer the affordance without
 *  caring whether the provider is mounted (the marketing 404, tests in
 *  isolation). */
// eslint-disable-next-line react-refresh/only-export-components
export function useSupportChat(): SupportChatContextValue {
  return (
    useContext(SupportChatContext) ?? {
      enabled: false,
      open: () => {},
      describeBroker: () => {},
    }
  );
}
